import React, { useEffect, useRef, useState, useMemo } from "react";
import api from "../api";

/* =======================
   CONFIG
======================= */

const ITEMS = ["face", "cap", "jacket", "whistle", "belt", "pants", "shoes"];

const REQUIRED_ITEMS = ["face", "jacket", "pants", "shoes"];

const LABELS = {
  face: "Face",
  cap: "Cap",
  jacket: "Jacket / Shirt",
  whistle: "Whistle",
  belt: "Belt",
  pants: "Pants",
  shoes: "Shoes",
};

const OFFLINE_QUEUE_KEY = "attendance_offline_queue";

/* =======================
   COMPONENT
======================= */

export default function StartShiftCompliance({ empId, siteId, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  /* =======================
     STATE
  ======================= */

  const [image, setImage] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(false);

  const [welcomeName, setWelcomeName] = useState("");

  const [meta, setMeta] = useState({
    latitude: null,
    longitude: null,
    mapUrl: "",
    timestamp: "",
  });

  // 🔑 CUMULATIVE COMPLIANCE STATE
  const [compliance, setCompliance] = useState(() =>
    ITEMS.reduce((acc, k) => {
      acc[k] = { status: "pending", confidence: null };
      return acc;
    }, {})
  );

  /* =======================
     DERIVED STATE
  ======================= */

  const missingItems = useMemo(() => {
    return REQUIRED_ITEMS.filter(
      (k) => compliance[k].status !== "passed"
    );
  }, [compliance]);

  const allPassed = missingItems.length === 0;

  /* =======================
     CAMERA START
  ======================= */

 useEffect(() => {
  const startCamera = async () => {
    if (streamRef.current) return; // already started
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      console.warn("Camera start failed", err);
    }
  };
  startCamera();
  return () => streamRef.current?.getTracks().forEach(t => t.stop());
}, []);


  /* =======================
     OFFLINE SYNC
  ======================= */

  useEffect(() => {
    const syncOffline = async () => {
      const q = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY)) || [];
      if (!q.length) return;

      for (const item of q) {
        try {
          await api.post(
            `${process.env.REACT_APP_URL}/solution/attendance/start-shift-compliance`,
            item
          );
        } catch {
          return;
        }
      }
      localStorage.removeItem(OFFLINE_QUEUE_KEY);
    };

    window.addEventListener("online", syncOffline);
    return () => window.removeEventListener("online", syncOffline);
  }, []);

  useEffect(() => {
  if (!previewMode && videoRef.current) {
    // Reattach stream if lost
    if (!videoRef.current.srcObject && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
    // Play safely
    videoRef.current.play().catch(() => {});
  }
}, [previewMode]);

  /* =======================
     CAPTURE
  ======================= */

  const capturePhoto = () => {

    if (!videoRef.current || !videoRef.current.videoWidth) return;

    navigator.geolocation.getCurrentPosition(async (pos) => {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0);

      const img = canvas.toDataURL("image/jpeg", 0.9);
      setImage(img);
      setPreviewMode(true);
      videoRef.current.pause();

      const now = new Date().toISOString();

      setMeta({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        mapUrl: `https://www.google.com/maps?q=${pos.coords.latitude},${pos.coords.longitude}`,
        timestamp: now,
      });
    });
  };

const retakePhoto = () => {
  setImage(null);
  setPreviewMode(false);
  // No need to manually attach stream here, useEffect handles it
};




  /* =======================
     CHECK (AI)
  ======================= */

  const submitForAnalysis = async () => {
    setLoading(true);

    const itemsToCheck =
      compliance.face.status === "pending"
        ? ITEMS
        : ITEMS.filter((k) => compliance[k].status !== "passed");

    const payload = {
      empId,
      siteId,
      imageBase64: image,
      itemsToCheck,
      meta,
      capturedAt: meta.timestamp,
      offlineCapturedAt: new Date().toISOString(),
    };

    if (!navigator.onLine) {
      const q = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY)) || [];
      q.push(payload);
      localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(q));
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_URL}/solution/attendance/start-shift-compliance`,
        payload
      );

      // SAFETY
      const face = data.face || {};

      // 🔑 FACE FIRST
      if (face.matched) {
        setWelcomeName(face.username); 
        setCompliance((prev) => ({
          ...prev,
          face: {
            status: "passed",
            confidence: face.confidence,
          },
        }));
      } else {
        alert(`❌ Face not matched.\nSimilarity: ${face.confidence}`);
      }

      // 🔑 MERGE RESULTS (CUMULATIVE)
      setCompliance((prev) => {
        const next = { ...prev };
        Object.entries(data.results || {}).forEach(([k, v]) => {
          if (v.detected) {
            next[k] = { status: "passed", confidence: v.confidence };
          }
        });
        return next;
      });

      setPreviewMode(false);
      setImage(null);
      //videoRef.current.play();
    } finally {
      setLoading(false);
    }
  };

  /* =======================
     SUBMIT FINAL
  ======================= */

  const submitFinal = () => {
    if (!allPassed) {
      const ok = window.confirm(
        `Missing items: ${missingItems.map((k) => LABELS[k]).join(", ")}.\nSubmit anyway?`
      );
      if (!ok) return;
    }

    alert("✅ Shift started successfully");
    onClose();
  };

  /* =======================
     UI
  ======================= */

  return (
    <div style={styles.screen}>
      <button onClick={onClose} style={styles.backBtn}>⬅ Back</button>

      {/*<h2 style={{ textAlign: "center" }}>🛡 Uniform Compliance</h2> */}

      {welcomeName && (
        <h3 style={{ textAlign: "center", color: "#00e676" }}>
          Welcome {welcomeName} !
        </h3>
      )}

      <div style={styles.gameArea}>
        <div style={styles.characterArea}>
          <HumanSVG highlights={mapGlow(compliance)} />
        </div>

        <div style={styles.cameraArea}>
          {previewMode ? (
            <img src={image} alt="preview" style={styles.video} />
          ) : (
            <video ref={videoRef} autoPlay muted style={styles.video} />
          )}

          <div style={styles.actions}>
            {!previewMode && !allPassed && (
              <button onClick={capturePhoto}>
                📸 Capture {missingItems.length ? missingItems.map((k) => LABELS[k]).join(", ") : ""}
              </button>
            )}

            {previewMode && (
              <>
                <button onClick={retakePhoto}>🔄 Retake</button>
                <button onClick={submitForAnalysis} disabled={loading}>
                  {loading ? "Checking..." : "✅ Check"}
                </button>
              </>
            )}

            {allPassed && (
              <button onClick={submitFinal}>🚀 Submit</button>
            )}
          </div>
        </div>
      </div>

      <style>{css}</style>
    </div>
  );
}

/* =======================
   HELPERS
======================= */

const mapGlow = (c) =>
  Object.fromEntries(
    Object.entries(c).map(([k, v]) => [
      k,
      v.status === "passed" ? "glow-success" : "",
    ])
  );

/* =======================
   SVG + STYLES (UNCHANGED)
======================= */

const HumanSVG = ({ highlights }) => (
  <svg viewBox="0 0 200 520" height="100%">
    <circle cx="100" cy="75" r="34" className={highlights.face} />
    <rect x="55" y="115" width="90" height="150" rx="24" className={highlights.jacket} />
    <rect x="55" y="260" width="90" height="12" className={highlights.belt} />
    <rect x="55" y="272" width="40" height="140" className={highlights.pants} />
    <rect x="105" y="272" width="40" height="140" className={highlights.pants} />
    <rect x="52" y="414" width="45" height="24" className={highlights.shoes} />
    <rect x="103" y="414" width="45" height="24" className={highlights.shoes} />
  </svg>
);

const styles = {
  screen: { background: "grey", color: "white", height: "100vh", padding: 12 },
  backBtn: { background: "none", color: "white", border: "none" },
  gameArea: { display: "grid", gridTemplateColumns: "1fr 1fr" },
  characterArea: { height: "80vh" },
  cameraArea: { padding: 12 },
  video: { width: "100%", borderRadius: 12 },
  actions: { display: "flex", gap: 10, marginTop: 10 },
};

const css = `
.glow-success { fill:#00e676 !important; filter: drop-shadow(0 0 10px #00e676); }
`;
